import { type ReactNode, Suspense, use } from "react";
import styles from "./Code.module.scss";
// The code palette's classes (CodePalette.ts), generated by the build into the merged stylesheet.
import "./codePalette.css";
import {
  findHighlightedCode,
  type HighlightedToken,
  type HighlightVariant,
} from "./HighlightedCode";
import { getLoadedShiki, loadShiki } from "./ShikiLoader";
import { renderTokens } from "./TokenRendering";

const ShikiTokens = (props: HighlightedTokensProps) => {
  const { value, language, variant, children } = props;
  if (!getLoadedShiki()?.isCodeLanguageLoaded(language)) use(loadShiki(language));
  return children(getLoadedShiki()!.highlightWithShiki(value, language, variant));
};

export interface HighlightedTokensProps {
  value: string;
  language: string;
  /** `inline` tokens also carry light-scheme colors, for `flattenInlineToken`. */
  variant?: HighlightVariant;
  children: (tokens: HighlightedToken[][]) => ReactNode;
  /** Shown while the browser loads Shiki or a grammar for code first rendered there. */
  fallback?: ReactNode;
}

/**
 * Renders the tokens of `value`. Only code that goes through Shiki gets a Suspense boundary: React's
 * streaming renderer moves large completed boundaries to the end of the HTML, so a boundary around
 * every block would show plain text first and swap the highlighted code in later.
 */
export const HighlightedTokens = (props: HighlightedTokensProps) => {
  const { value, language, variant, children, fallback } = props;
  const tokens = findHighlightedCode(value, language, variant);
  if (tokens) return children(tokens);
  return (
    <Suspense fallback={fallback}>
      <ShikiTokens {...props} />
    </Suspense>
  );
};

const plainLines = (value: string) =>
  value.split("\n").map((line, index) => (
    // Lines are positional and never reordered.
    <span key={index} className={styles.line}>
      {line}
      {"\n"}
    </span>
  ));

const renderLines = (tokens: HighlightedToken[][]) =>
  tokens.map((line, index) => (
    // Lines are positional and never reordered.
    <span key={index} className={styles.line}>
      {renderTokens(line, "\n")}
    </span>
  ));

export interface StaticCodeProps {
  value: string;
  language: string;
  fontSize: number;
  softwrap: boolean;
}

const StaticCode = (props: StaticCodeProps) => {
  const { value, language, fontSize, softwrap } = props;
  return (
    <pre
      className={styles.static}
      data-softwrap={softwrap ? "true" : undefined}
      style={{ fontSize, lineHeight: 1.65 }}
    >
      <code>
        <HighlightedTokens value={value} language={language} fallback={plainLines(value)}>
          {renderLines}
        </HighlightedTokens>
      </code>
    </pre>
  );
};

export default StaticCode;
